import React, { useState, useEffect } from 'react';
import { apiService } from '../../../../services/api';
import { FornecedorDadoBancario } from '../../../../types/empenhos';
import Select from 'react-select';
import { toast } from 'react-toastify';

interface SelecionarDadosBancariosProps {
  idFornecedor: number;
  idConta: number;
  selectedId?: number;
  onSelect: (id: number, dados: FornecedorDadoBancario) => void;
  onNewCreated?: (dados: FornecedorDadoBancario) => void;
  disabled?: boolean;
}

interface FormData {
  tipo: 'CONTA_CORRENTE' | 'CONTA_POUPANCA' | 'PIX';
  banco?: string;
  agencia?: string;
  agencia_digito?: string;
  conta?: string;
  conta_digito?: string;
  titular?: string;
  cpf_cnpj_titular?: string;
  pix_chave?: string;
  pix_tipo_chave?: 'CPF' | 'CNPJ' | 'EMAIL' | 'TELEFONE' | 'ALEATORIA';
  principal: boolean;
  observacoes?: string;
}

const SelecionarDadosBancarios: React.FC<SelecionarDadosBancariosProps> = ({
  idFornecedor,
  idConta,
  selectedId,
  onSelect,
  onNewCreated,
  disabled = false
}) => {
  const [dadosBancarios, setDadosBancarios] = useState<FornecedorDadoBancario[]>([]);
  const [selectedData, setSelectedData] = useState<FornecedorDadoBancario | null>(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    tipo: 'CONTA_CORRENTE',
    principal: false
  });

  useEffect(() => {
    carregarDadosBancarios();
  }, [idFornecedor, idConta]);

  useEffect(() => {
    // Se não tem dados bancários, automaticamente abre o formulário de cadastro
    if (dadosBancarios.length === 0 && !isLoading) {
      setIsFormVisible(true);
    }

    if (dadosBancarios.length > 0 && !selectedData) {
      // Seleciona automaticamente o principal ou o primeiro
      const principal = dadosBancarios.find(d => d.principal);
      if (principal) {
        setSelectedData(principal);
        onSelect(principal.id, principal);
      } else {
        setSelectedData(dadosBancarios[0]);
        onSelect(dadosBancarios[0].id, dadosBancarios[0]);
      }
    }
  }, [dadosBancarios, selectedData, onSelect, isLoading]);

  const carregarDadosBancarios = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getDadosBancariosFornecedor(idFornecedor, idConta);
      if (response.success && response.data) {
        setDadosBancarios(response.data);

        // Seleciona o selectedId se fornecido
        if (selectedId) {
          const selected = response.data.find(d => d.id === selectedId);
          if (selected) {
            setSelectedData(selected);
            onSelect(selectedId, selected);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados bancários:', error);
      toast.error('Erro ao carregar dados bancários do fornecedor');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (dados: FornecedorDadoBancario) => {
    setSelectedData(dados);
    onSelect(dados.id, dados);
  };

  const handleFormChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Limpar campos específicos quando tipo muda
      ...(field === 'tipo' && {
        banco: undefined,
        agencia: undefined,
        agencia_digito: undefined,
        conta: undefined,
        conta_digito: undefined,
        pix_chave: undefined,
        pix_tipo_chave: value === 'PIX' ? 'CPF' : undefined
      })
    }));
  };

  const handleSubmit = async () => {
    if (!formData.titular?.trim()) {
      toast.error('O titular é obrigatório');
      return;
    }

    if (formData.tipo === 'PIX' && !formData.pix_chave?.trim()) {
      toast.error('A chave PIX é obrigatória');
      return;
    }

    try {
      setIsSaving(true);

      // Validar campos específicos do tipo
      if (formData.tipo === 'CONTA_CORRENTE' || formData.tipo === 'CONTA_POUPANCA') {
        if (!formData.banco?.trim() || !formData.agencia?.trim() || !formData.conta?.trim() || !formData.agencia_digito?.trim() || !formData.conta_digito?.trim() || !formData.cpf_cnpj_titular?.trim()) {
          toast.error('Banco, agência e dígito, conta e dígito e CPF/CNPJ do titular são obrigatórios e devem ser preenchidos.');
          return;
        }
      }

      const newData = {
        ...formData,
        pix_tipo_chave: formData.tipo === 'PIX' ? (formData.pix_tipo_chave || 'CPF') : undefined,
        id_fornecedor: idFornecedor,
        id_conta: idConta,
        ativo: true
      };

      // Se definido como principal, definir outros como não principais
      if (formData.principal) {
        // Ignorar aqui, pois após criar faremos a chamada para set principal
      }

      const response = await apiService.createDadosBancarios(newData);

      if (response.success && response.data) {
        const dadosCriados: FornecedorDadoBancario = {
          ...response.data,
          principal: formData.principal
        };

        setDadosBancarios(prev => [...prev, dadosCriados]);
        setIsFormVisible(false);

        if (onNewCreated) {
          onNewCreated(dadosCriados);
        }

        // Se for principal, selecionar automaticamente
        if (formData.principal) {
          setSelectedData(dadosCriados);
          onSelect(dadosCriados.id, dadosCriados);
        }

        toast.success('Dados bancários criados com sucesso');
      }
    } catch (error) {
      console.error('Erro ao criar dados bancários:', error);
      toast.error('Erro ao criar dados bancários');
    } finally {
      setIsSaving(false);
    }
  };

  const renderFormulario = () => {
    if (!isFormVisible) return null;

    return (
      <div className="card mb-3">
        <div className="card-header">
          <h6 className="mb-0">
            <i className="bi bi-plus-circle me-2"></i>
            Adicionar Novo Dado Bancário
          </h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Tipo de Conta *</label>
              <Select
                options={[
                  { value: 'CONTA_CORRENTE', label: 'Conta Corrente' },
                  { value: 'CONTA_POUPANCA', label: 'Conta Poupança' },
                  { value: 'PIX', label: 'PIX' }
                ]}
                value={{ value: formData.tipo, label: formData.tipo === 'CONTA_CORRENTE' ? 'Conta Corrente' : formData.tipo === 'CONTA_POUPANCA' ? 'Conta Poupança' : 'PIX' }}
                onChange={(option) => handleFormChange('tipo', option?.value as any)}
                isDisabled={disabled}
                className="form-control-select"
              />
            </div>

            <div className="col-md-6 mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="principalCheck"
                  checked={formData.principal}
                  onChange={(e) => handleFormChange('principal', e.target.checked)}
                  disabled={disabled}
                />
                <label className="form-check-label" htmlFor="principalCheck">
                  Definir como principal
                </label>
              </div>
            </div>
          </div>

          {/* Campos dinâmicos por tipo */}
          {formData.tipo === 'CONTA_CORRENTE' || formData.tipo === 'CONTA_POUPANCA' ? (
            <>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Banco *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.banco || ''}
                    onChange={(e) => handleFormChange('banco', e.target.value)}
                    disabled={disabled}
                    placeholder="Ex: Banco do Brasil"
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Agência *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.agencia || ''}
                    onChange={(e) => handleFormChange('agencia', e.target.value)}
                    disabled={disabled}
                    placeholder="Número da agência"
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Dígito Agência</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.agencia_digito || ''}
                    onChange={(e) => handleFormChange('agencia_digito', e.target.value)}
                    disabled={disabled}
                    maxLength={2}
                    placeholder="Dígito"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Conta *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.conta || ''}
                    onChange={(e) => handleFormChange('conta', e.target.value)}
                    disabled={disabled}
                    placeholder="Número da conta"
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Dígito Conta</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.conta_digito || ''}
                    onChange={(e) => handleFormChange('conta_digito', e.target.value)}
                    disabled={disabled}
                    maxLength={2}
                    placeholder="Dígito"
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Titular *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.titular || ''}
                    onChange={(e) => handleFormChange('titular', e.target.value)}
                    disabled={disabled}
                    placeholder="Nome completo"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">CPF/CNPJ Titular</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.cpf_cnpj_titular || ''}
                    onChange={(e) => handleFormChange('cpf_cnpj_titular', e.target.value)}
                    disabled={disabled}
                    placeholder="CPF ou CNPJ"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Tipo de Chave PIX *</label>
                  <Select
                    options={[
                      { value: 'CPF', label: 'CPF' },
                      { value: 'CNPJ', label: 'CNPJ' },
                      { value: 'EMAIL', label: 'EMAIL' },
                      { value: 'TELEFONE', label: 'TELEFONE' },
                      { value: 'ALEATORIA', label: 'ALEATORIA' }
                    ]}
                    value={{ value: formData.pix_tipo_chave || 'CPF', label: formData.pix_tipo_chave || 'CPF' }}
                    onChange={(option) => handleFormChange('pix_tipo_chave', option?.value as any)}
                    isDisabled={disabled}
                    className="form-control-select"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Chave PIX *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.pix_chave || ''}
                    onChange={(e) => handleFormChange('pix_chave', e.target.value)}
                    disabled={disabled}
                    placeholder="Chave PIX"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Titular *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.titular || ''}
                    onChange={(e) => handleFormChange('titular', e.target.value)}
                    disabled={disabled}
                    placeholder="Nome completo"
                  />
                </div>
              </div>
            </>
          )}

          <div className="mb-3">
            <label className="form-label">Observações</label>
            <textarea
              className="form-control"
              value={formData.observacoes || ''}
              onChange={(e) => handleFormChange('observacoes', e.target.value)}
              disabled={disabled}
              rows={2}
              placeholder="Informações adicionais"
            />
          </div>

          <div className="text-end">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={() => setIsFormVisible(false)}
              disabled={disabled}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSaving || disabled}
            >
              {isSaving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDadosExistentes = () => {
    if (isLoading) {
      return <div className="text-center py-3">Carregando dados bancários...</div>;
    }

    if (dadosBancarios.length === 0) {
      return (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          Nenhum dado bancário encontrado para este fornecedor.
          <button
            type="button"
            className="btn btn-sm btn-primary ms-2"
            onClick={() => setIsFormVisible(true)}
            disabled={disabled}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Cadastrar agora
          </button>
        </div>
      );
    }

    return (
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">Dados Bancários Disponíveis</h6>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => setIsFormVisible(true)}
            disabled={disabled}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Adicionar Novo
          </button>
        </div>

        <div className="row">
          {dadosBancarios.map(dado => (
            <div key={dado.id} className="col-md-6 mb-3">
              <div
                className={`card ${selectedData?.id === dado.id ? 'border-primary' : ''}`}
                style={{ cursor: disabled ? 'default' : 'pointer' }}
                onClick={() => !disabled && handleSelect(dado)}
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="mb-0">
                        {dado.tipo === 'CONTA_CORRENTE' && 'Conta Corrente'}
                        {dado.tipo === 'CONTA_POUPANCA' && 'Conta Poupança'}
                        {dado.tipo === 'PIX' && 'PIX'}
                      </h6>
                      {dado.principal && (
                        <span className="badge bg-primary ms-2">Principal</span>
                      )}
                    </div>
                  </div>

                  {dado.tipo === 'CONTA_CORRENTE' || dado.tipo === 'CONTA_POUPANCA' ? (
                    <>
                      <p className="mb-1">
                        <strong>Banco:</strong> {dado.banco}
                      </p>
                      <p className="mb-1">
                        <strong>Agência:</strong> {dado.agencia}{dado.agencia_digito && `- ${dado.agencia_digito}`}
                      </p>
                      <p className="mb-1">
                        <strong>Conta:</strong> {dado.conta}{dado.conta_digito && `- ${dado.conta_digito}`}
                      </p>
                      <p className="mb-0">
                        <strong>Titular:</strong> {dado.titular}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="mb-1">
                        <strong>Chave:</strong> {dado.pix_tipo_chave} - {dado.pix_chave}
                      </p>
                      <p className="mb-0">
                        <strong>Titular:</strong> {dado.titular}
                      </p>
                    </>
                  )}

                  <div className="mt-3">
                    <small className="text-muted">
                      {selectedData?.id === dado.id ? (
                        <span className="text-success">
                          <i className="bi bi-check-circle me-1"></i>Selecionado
                        </span>
                      ) : (
                        <span>Clique para selecionar</span>
                      )}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <label className="form-label">
        <strong>Dados Bancários para Pagamento *</strong>
      </label>
      <div className="border rounded p-3 bg-light">
        {renderDadosExistentes()}
        {renderFormulario()}
      </div>
    </div>
  );
};

export default SelecionarDadosBancarios;